import React, { useState } from 'react'

import { Sidebar, SidebarContent, SidebarHeader } from '@/components/ui/sidebar'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { ClipboardList, Users } from 'lucide-react'

type SidebarPanel = 'game' | 'chat'

interface OGESidebarProps {
  header: React.ReactNode
  content: React.ReactNode
  chatContent?: React.ReactNode
  initialPanel?: SidebarPanel
  activePanel?: SidebarPanel
  onActivePanelChange?: (panel: SidebarPanel) => void
}

const OGESidebar: React.FC<OGESidebarProps> = ({
  header,
  content,
  chatContent,
  initialPanel = 'game',
  activePanel: controlledPanel,
  onActivePanelChange,
}) => {
  const [internalPanel, setInternalPanel] = useState<SidebarPanel>(initialPanel)
  const isControlled = controlledPanel !== undefined
  const activePanel = isControlled ? controlledPanel : internalPanel

  const handlePanelChange = (panel: SidebarPanel) => {
    if (!isControlled) {
      setInternalPanel(panel)
    }
    onActivePanelChange?.(panel)
  }

  return (
    <Sidebar collapsible='offcanvas' className='w-80 border-none bg-[#FAFCFF] shadow-[0_4px_24px_rgba(0,139,192,0.12)]'>
      <SidebarHeader className='bg-[#FAFCFF] min-h-16 w-full py-0 px-7'>
        {header}
      </SidebarHeader>
      {chatContent && (
        <div className='group-data-[state=collapsed]:hidden flex gap-1 px-4 py-2 border-b border-slate-100 bg-[#FAFCFF]'>
          <button
            onClick={() => handlePanelChange('game')}
            className={cn(
              'flex-1 h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all',
              activePanel === 'game'
                ? 'bg-[#E6ECF2] text-slate-900 border border-[#B9C8D8] shadow-sm'
                : 'text-slate-500 border border-transparent hover:bg-[#EEF2F7]'
            )}
          >
            <ClipboardList className='w-3.5 h-3.5' />
            Operations
          </button>
          <button
            onClick={() => handlePanelChange('chat')}
            className={cn(
              'flex-1 h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all',
              activePanel === 'chat'
                ? 'bg-[#E6ECF2] text-slate-900 border border-[#B9C8D8] shadow-sm'
                : 'text-slate-500 border border-transparent hover:bg-[#EEF2F7]'
            )}
          >
            <Users className='w-3.5 h-3.5' />
            Collaboration
          </button>
        </div>
      )}

      {chatContent && activePanel === 'chat' ? (
        <div className='group-data-[state=collapsed]:hidden flex-1 min-h-0 overflow-hidden flex flex-col bg-[#FAFCFF]'>
          {chatContent}
        </div>
      ) : (
        <ScrollArea className='group-data-[state=collapsed]:hidden flex-1 w-full transition-opacity duration-200'>
          <SidebarContent className='bg-[#FAFCFF] pt-[3.2rem] px-2 pb-12 gap-2'>
            {content}
          </SidebarContent>
        </ScrollArea>
      )}
    </Sidebar>
  )
}

export default OGESidebar
